import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import BPGrid from '../../../BusinessPartners/BPTestTable';
import { useDropdownOptions } from '../../queryHooks/useDropdownOptions';

const ComplianceChecklistModal = ({
  title,
  heading,
  show,
  isEditable,
  disableViewButton,
  rows = [],
  onClose,
  onSave,
  onView,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [updatedData, setUpdatedData] = useState({});

  const { staticDropdowns } = useDropdownOptions();
  const { countries } = staticDropdowns.data || {};
  const countryList = useMemo(
    () => (countries || []).reduce((acc, { id, data }) => ({ ...acc, [id]: data }), {}),
    [countries]
  );

  const newValues = Object.values(updatedData);
  const updatedDataIsEmpty = !!newValues.length && newValues.every((item) => !item);

  const handleEditOrDiscard = () => {
    if (isEditing) {
      setUpdatedData({});
    }
    setIsEditing((prev) => !prev);
  };

  const handleClose = () => {
    setIsEditing(false);
    onClose();
    setUpdatedData({});
  };

  const handleSave = async () => {
    await onSave?.(updatedData);
    setIsEditing(false);
    setUpdatedData({});
  };

  const onChange = (key, newValue, previousValue) => {
    if (newValue === previousValue) {
      //remove the value from the updated data object
      setUpdatedData((prev) => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    } else {
      setUpdatedData((prev) => ({ ...prev, [key]: newValue }));
    }
  };

  // Reset edit state when modal closes
  useEffect(() => {
    if (!show) {
      setIsEditing(false);
      setUpdatedData({});
    }
  }, [show]);

  return (
    <Modal show={show} onHide={handleClose} centered dialogClassName="qa-wide-modal">
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
        <button type="button" className="btn-close" aria-label="Close" onClick={handleClose} />
      </Modal.Header>

      <Modal.Body>
        {heading && <div className="bp-section-heading">{heading}</div>}
        <div>
          <BPGrid numCols={2} isEditing={isEditing}>
            {rows.map((row) => {
              const isCountry = row.key === 'country';
              return (
                <BPGrid.Row
                  subTitle={row.title}
                  value={row.value || ''}
                  isEditingValue={isEditing && !row.readOnly}
                  key={row.title}
                  hasHeaderError={row.hasError}
                  onChange={(_, newValue) => onChange(row.key, newValue, row.value)}
                  className={isEditing && row.readOnly ? 'grey-bg' : ''}
                  valueType={isCountry ? 'dropdown' : 'string'}
                  dropdownValues={isCountry ? countryList : []}
                />
              );
            })}
          </BPGrid>
        </div>
        {updatedDataIsEmpty && <div style={{ color: 'red' }}>Invalid data - all fields are required.</div>}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex w-100 justify-content-between">
          <button className="epo-continue-button small-button" type="button" onClick={handleClose}>
            Cancel
          </button>

          {isEditable ? (
            <div className="d-flex gap-2">
              <button className="blue-button-newload small-button red" type="button" onClick={handleEditOrDiscard}>
                {isEditing ? 'Discard' : 'Edit'}
              </button>
              <button
                className="blue-button-newload small-button"
                type="button"
                onClick={() => handleSave()}
                disabled={!isEditing || !Object.keys(updatedData).length || updatedDataIsEmpty}
              >
                Save
              </button>
            </div>
          ) : (
            <button
              className="blue-button-newload small-button"
              type="button"
              onClick={() => onView()}
              disabled={disableViewButton || !onView}
            >
              View
            </button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ComplianceChecklistModal;
